import type { ChangeEvent } from "react";
import { useEffect, useMemo } from "react";
import type { VideoUploaderFormFields } from ".";
import { debounce } from "lodash";
import { useFormContext } from "react-hook-form";
import type { UseMutationResult } from "react-query";
import { t } from "@/i18n-js/instance";
import type { ApiError } from "@/react/config/CustomErrors";
import { isValidURL } from "@/react/helpers/urlHelpers/isValidUrl";
import type { EmbedResponse } from "./useVideoUploader";

export interface VideoUploaderFieldsProps {
  createEmbedMutation: UseMutationResult<EmbedResponse, ApiError, string>;
  name: string;
}

export const useVideoUploaderFields = ({
  name,
  createEmbedMutation,
}: VideoUploaderFieldsProps) => {
  const { setError, setValue, watch } =
    useFormContext<VideoUploaderFormFields>();
  const source = watch(`${name}.source`, "upload");

  const debouncedCreateEmbed = useMemo(
    () =>
      debounce((url: string) => {
        if (!isValidURL(url)) {
          setError("url", {
            type: "manual",
            message: t("builder_ui.prop_type.video_upload.errors.invalid_url"),
          });
          return;
        }
        createEmbedMutation.mutate(url);
      }, 1000),
    [createEmbedMutation, setError],
  );

  const handleOnChangeUrl = (event: ChangeEvent<HTMLInputElement>) => {
    const url = event.target.value;
    debouncedCreateEmbed(url);
  };

  const handleOnClickRemove = () => {
    /* TODO: Add confirmation dialog */
    const isConfirmed = confirm(
      t("builder_ui.prop_type.video_upload.remove_video_confirmation"),
    );
    if (!isConfirmed) {
      return;
    }
    setValue(name, {
      source: "upload",
      signed_id: "",
      customCoverImage: null,
      url: "",
      hasCustomCoverImage: false,
      isVideoMuted: false,
      isVideoLooped: false,
      hasAltText: false,
      videoAltText: "",
      customCoverImageSignedId: "",
      html: "",
    });
  };

  useEffect(
    () => () => {
      debouncedCreateEmbed.cancel();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debouncedCreateEmbed is memorized
    [],
  );

  return {
    source,
    handleOnChangeUrl,
    handleOnClickRemove,
  };
};
